import { useMemo } from 'react'
import chunk from 'lodash/chunk'
import {
  keepPreviousData,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { PersonalPage, PersonalDetails, ImageUrlResponse } from '@chilipiper/api-client-workspace'
import { useFireSessionContext } from '@chilipiper/fire-session'
import { Update1 } from '@chilipiper/api-client-workspace/data-contracts'
import { useApiClient } from './apiClientContext'

const personalDetailsKey = (tenantId: string, userId: string) => [
  'personal-details',
  'tenant',
  tenantId,
  'user',
  userId,
]

export const useFindPersonalDetailsById = (userId: string, options?: { suspense?: boolean }) => {
  const { tenantId } = useFireSessionContext()
  const { workspaceV3 } = useApiClient()

  return useQuery({
    queryKey: personalDetailsKey(tenantId, userId),
    queryFn: () => workspaceV3.findPersonalDetailsV3(tenantId, userId),
    retryOnMount: false,
    enabled: userId !== '',
    refetchOnWindowFocus: false,
    ...options,
  })
}

export const useBatchedPersonalDetails = (
  ids: string[] = [],
  params?: { keepPreviousData?: boolean }
) => {
  const { tenantId } = useFireSessionContext()
  const { workspaceV4 } = useApiClient()
  return useQuery({
    queryKey: ['personal-details', { users: ids }],
    queryFn: () =>
      workspaceV4.findPersonalDetailsOfListOfUsersV4Put(tenantId, {
        ids,
      }),
    enabled: ids.length > 0,
    ...params,
  })
}

const PERSONAL_DETAILS_BATCH_SIZE = 50

type PersonalDetailsBatch = {
  data: Record<string, PersonalDetails> | undefined
  isPending: boolean
}

const combinePersonalDetailsBatches = (batches: PersonalDetailsBatch[]) => {
  const data: Record<string, PersonalDetails> = {}
  batches.forEach(batch => Object.assign(data, batch.data))
  return { data, isPending: batches.some(batch => batch.isPending) }
}

/**
 * Personal details for a list of user ids that grows as a table pages in rows. The ids are split
 * into fixed-size batches in order of appearance, so a new page only fetches the batch that gained
 * ids, and a batch keeps its previous result while it refetches: names already on screen never
 * blank out.
 */
export const usePersonalDetailsByIds = (ids: string[]) => {
  const { tenantId } = useFireSessionContext()
  const { workspaceV4 } = useApiClient()
  const batches = useMemo(() => chunk(ids, PERSONAL_DETAILS_BATCH_SIZE), [ids])

  return useQueries({
    queries: batches.map(batch => ({
      queryKey: ['personal-details', 'batch', tenantId, batch],
      queryFn: () => workspaceV4.findPersonalDetailsOfListOfUsersV4Put(tenantId, { ids: batch }),
      placeholderData: keepPreviousData,
    })),
    combine: combinePersonalDetailsBatches,
  })
}

export const useSetPersonalDetails = () => {
  const queryClient = useQueryClient()
  const { tenantId, userId } = useFireSessionContext()
  const key = personalDetailsKey(tenantId, userId)
  const { workspaceV3 } = useApiClient()

  return useMutation({
    mutationFn: (personalDetails: PersonalDetails) =>
      workspaceV3.setPersonalDetailsV3(tenantId, userId, personalDetails),
    onMutate: async updates => {
      await queryClient.cancelQueries({ queryKey: key })
      const previousPersonalDetails = queryClient.getQueryData<PersonalDetails>(key)
      queryClient.setQueryData(key, updates)
      return { previousPersonalDetails, updatedPersonalDetails: updates }
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: key }),
    onError: (err, updates, context) => {
      queryClient.setQueryData(key, context?.previousPersonalDetails)
    },
  })
}

const personalPageKey = (tenantId: string, userId: string) => ['personal-page', tenantId, userId]

export const usePersonalPage = () => {
  const { tenantId, userId } = useFireSessionContext()
  const { workspaceV3 } = useApiClient()

  return useQuery({
    queryKey: personalPageKey(tenantId, userId),
    queryFn: () => {
      return workspaceV3.getPersonalPageV3(tenantId, userId)
    },
    refetchOnWindowFocus: false,
  })
}

export const useSetPersonalPage = () => {
  const queryClient = useQueryClient()
  const { tenantId, userId } = useFireSessionContext()
  const key = personalPageKey(tenantId, userId)
  const { workspaceV3 } = useApiClient()

  return useMutation({
    mutationFn: (personalPageUpdates: Update1) => {
      return workspaceV3.setPersonalPageV3(tenantId, userId, personalPageUpdates)
    },
    onMutate: async updates => {
      await queryClient.cancelQueries({ queryKey: key })
      const previousPersonalPage = queryClient.getQueryData<PersonalPage>(key)
      const newPersonalPage = { ...previousPersonalPage, ...updates }
      queryClient.setQueryData(key, newPersonalPage)
      return { newPersonalPage, previousPersonalPage }
    },
    onError: (err, updates, context) => {
      queryClient.setQueryData(key, context?.previousPersonalPage)
    },
  })
}

export const useDeletePersonalPage = () => {
  const queryClient = useQueryClient()
  const { tenantId, userId } = useFireSessionContext()
  const key = personalPageKey(tenantId, userId)
  const { workspaceV3 } = useApiClient()

  return useMutation({
    mutationFn: () => {
      return workspaceV3.deletePersonalPageV3(tenantId, userId)
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: key })
      const previousPersonalPage = queryClient.getQueryData<PersonalPage>(key)
      return { previousPersonalPage }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: key })
    },
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: key })
    },
    onError: (err, updates, context) => {
      queryClient.setQueryData(key, context?.previousPersonalPage)
    },
  })
}

export const useUserPageSettings = () => {
  const { tenantId } = useFireSessionContext()
  const { workspace } = useApiClient()

  return useQuery({
    queryKey: ['user-page-settings', tenantId],
    queryFn: () => workspace.getUserPageSettingsUser(tenantId),
    refetchOnWindowFocus: false,
  })
}

const profilePictureKey = (tenantId: string, userId: string) => [
  'profile-picture',
  tenantId,
  userId,
]

export const useProfilePictureUrl = (userId?: string) => {
  const { tenantId } = useFireSessionContext()
  const { workspaceV3 } = useApiClient()

  return useQuery({
    queryKey: profilePictureKey(tenantId, userId as string),
    queryFn: async () => {
      const data = await workspaceV3.getPictureOfUserV3(tenantId, userId as string)
      return data.url
    },
    enabled: !!userId,
  })
}

export const useChangeProfilePicture = () => {
  const queryClient = useQueryClient()
  const { tenantId, userId } = useFireSessionContext()
  const { workspaceV3 } = useApiClient()
  const queryKey = profilePictureKey(tenantId, userId)

  return useMutation<ImageUrlResponse, { status: number; statusText: string } | unknown, File>({
    mutationFn: (image: File) => {
      return workspaceV3.setUserPictureV3(tenantId, userId, {
        image,
      })
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey })
    },
  })
}

export const useDeleteProfilePicture = () => {
  const queryClient = useQueryClient()
  const { tenantId, userId } = useFireSessionContext()
  const { workspaceV3 } = useApiClient()
  const queryKey = profilePictureKey(tenantId, userId)

  return useMutation({
    mutationFn: () => {
      return workspaceV3.deleteUserPictureV3(tenantId, userId)
    },
    onMutate: () => {
      queryClient.setQueryData(queryKey, '')
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey })
    },
  })
}
